"use client";

/**
 * Root Component
 * @apal/web-apal
 *
 * Sets up all providers for the APAL portal application.
 */

import * as React from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Toaster } from "sonner";

import { createApalTheme } from "@apal/shared-theme";
import { AuthProvider, type AuthApi, type RegisterData } from "@apal/shared-auth";
import { authApi, handleAuthError } from "./lib/auth";
import { appConfig } from "./config/app";

// Create the APAL theme from shared-theme package
const theme = createApalTheme();

// Create the auth API adapter that matches @apal/shared-auth interface
const authApiAdapter: AuthApi = {
  isAuthenticated: () => authApi.isAuthenticated(),
  login: async (params) => authApi.login(params),
  register: async (data: RegisterData) =>
    authApi.register({
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      userType: data.userType,
      companyName: data.companyName,
      inviteCode: data.inviteCode,
    }),
  logout: async () => authApi.logout(),
  refreshToken: async () => authApi.refreshToken(),
  forgotPassword: async (params) => authApi.forgotPassword(params),
  resetPassword: async (params) => authApi.resetPassword(params),
  getProfile: async () => authApi.getProfile(),
  // REQ-BVDR-AUTH-BOOTSTRAP-401-LOOP-FIX (PC-011): non-navigating bootstrap path
  getProfileNoRedirect: async () => authApi.getProfileNoRedirect(),
  hasRefreshToken: () => authApi.hasRefreshToken(),
  clearTokens: () => authApi.clearTokens(),
};

export interface RootProps {
  children: React.ReactNode;
}

export function Root({ children }: RootProps): React.JSX.Element {
  return (
    <HelmetProvider>
      <Helmet>
        <title>{appConfig.name}</title>
        <meta content={appConfig.themeColor} name="theme-color" />
      </Helmet>
      <AuthProvider authApi={authApiAdapter} errorHandler={handleAuthError}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
          <Toaster position="bottom-right" />
        </ThemeProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
